import { ArrowDownLeft, ArrowUpRight, Repeat, Check } from "lucide-react";

const StatusPill = ({ label }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
    <Check size={11} strokeWidth={2.5} /> {label}
  </span>
);

const TxnCard = ({ icon: Icon, title, sub, tone = "blue", className = "", delayClass = "" }) => (
  <div
    className={`flex items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white/95 px-4 py-3 shadow-lg shadow-slate-900/[0.06] backdrop-blur ${className} ${delayClass}`}
  >
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
        tone === "green" ? "bg-emerald-50 text-emerald-600" : tone === "navy" ? "bg-[#EFF5FD] text-[#1E3A6E]" : "bg-[#EFF5FD] text-[#2E7CD6]"
      }`}
    >
      <Icon size={16} strokeWidth={1.5} />
    </span>
    <div>
      <p className="whitespace-nowrap text-[13px] font-semibold text-[#0F172A]">{title}</p>
      <p className="whitespace-nowrap text-xs text-[#64748B]">{sub}</p>
    </div>
    <div className="ml-auto pl-1">
      <StatusPill label="Completed" />
    </div>
  </div>
);

const HeroVisual = () => (
  <div className="relative mx-auto w-full max-w-[520px] px-2 pb-14 pt-12 sm:px-0" data-testid="hero-visual">
    <div className="sw-glow absolute -inset-10 -z-10" aria-hidden="true" />
    <svg className="absolute inset-0 -z-10 h-full w-full" viewBox="0 0 520 520" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <circle cx="260" cy="260" r="238" stroke="#2E7CD6" strokeOpacity="0.18" strokeWidth="1.2" className="sw-flow-line" />
      <circle cx="260" cy="260" r="180" stroke="#1E3A6E" strokeOpacity="0.12" strokeWidth="1.2" className="sw-flow-line" style={{ animationDirection: "reverse" }} />
    </svg>

    {/* Main balance card */}
    <div className="sw-animate-float-slow relative z-10 mx-auto w-full rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-2xl shadow-slate-900/[0.08] sm:w-[88%] sm:p-7" data-testid="hero-balance-card">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-[#64748B]">Available Balance</p>
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EFF5FD]">
          <span className="h-2 w-2 rounded-full bg-[#2E7CD6]" />
        </span>
      </div>
      <p className="mt-2 font-heading text-[26px] font-extrabold tracking-tight text-[#0F172A] sm:text-[32px]" data-testid="hero-balance-amount">
        ₦569,842,150.75
      </p>
      <div className="mt-5 grid grid-cols-4 gap-2">
        {[
          { c: "USD", v: "212,480" },
          { c: "NGN", v: "318.4M" },
          { c: "KES", v: "27.6M" },
          { c: "ZAR", v: "3.8M" },
        ].map((x) => (
          <div key={x.c} className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-2.5 text-center">
            <p className="text-[11px] font-semibold text-[#1E3A6E]">{x.c}</p>
            <p className="mt-0.5 text-[11px] text-[#64748B]">{x.v}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-[#F1F5F9]">
        <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-[#1E3A6E] to-[#2E7CD6]" />
      </div>
    </div>

    {/* Floating transaction cards */}
    <div className="sw-animate-float absolute -top-1 left-0 z-20 sm:-left-6" data-testid="hero-txn-received">
      <div className="origin-top-left scale-[0.85] sm:scale-100">
        <TxnCard icon={ArrowDownLeft} tone="green" title="Payment received" sub="+ $12,500.00" />
      </div>
    </div>
    <div className="sw-animate-float-rev absolute -right-1 top-[62%] z-20 sm:-right-10" data-testid="hero-txn-transfer">
      <div className="origin-top-right scale-[0.85] sm:scale-100">
        <TxnCard icon={ArrowUpRight} tone="navy" title="International transfer" sub="$8,250 → NGN" />
      </div>
    </div>
    <div className="sw-animate-float absolute -bottom-2 left-[4%] z-20" style={{ animationDelay: "1.4s" }} data-testid="hero-txn-fx">
      <div className="origin-bottom-left scale-[0.85] sm:scale-100">
        <TxnCard icon={Repeat} title="FX Conversion" sub="USD → KES" />
      </div>
    </div>
  </div>
);

export default HeroVisual;
